import React, { useEffect, useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { adminOrders, adminUpdateOrder } from "../../services/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";

const STATUS = {
  pending_payment: { label: "Zahlung ausstehend", cls: "bg-amber-100 text-amber-800" },
  paid: { label: "Bezahlt", cls: "bg-green-100 text-green-800" },
  shipped: { label: "Versendet", cls: "bg-blue-100 text-blue-800" },
  completed: { label: "Abgeschlossen", cls: "bg-emerald-100 text-emerald-800" },
  payment_failed: { label: "Zahlung fehlgeschlagen", cls: "bg-red-100 text-red-800" },
  cancelled: { label: "Storniert", cls: "bg-gray-200 text-gray-700" },
  received: { label: "Eingegangen", cls: "bg-blue-100 text-blue-800" },
};

const NEXT_ACTIONS = { paid: ["shipped", "cancelled"], shipped: ["completed"], pending_payment: ["cancelled"] };

const OrderRow = ({ order, onStatus }) => {
  const [open, setOpen] = useState(false);
  const s = STATUS[order.status] || { label: order.status, cls: "bg-gray-100 text-gray-700" };
  const addr = order.address || {};
  return (
    <div className="bg-white rounded-2xl border border-border" data-testid="admin-order-row">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center justify-between gap-3 p-4 text-left">
        <div className="flex items-center gap-3 min-w-0">
          {open ? <ChevronUp className="w-4 h-4 text-ink/40 shrink-0" /> : <ChevronDown className="w-4 h-4 text-ink/40 shrink-0" />}
          <span className="font-mono text-xs text-ink/40">#{order.id.slice(0, 8)}</span>
          <span className="text-sm text-ink/80 truncate">{addr.firstName} {addr.lastName}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink/50">{new Date(order.created_at).toLocaleDateString("de-CH")}</span>
          <span className="font-semibold text-wine text-sm">CHF {order.total?.toFixed(2)}</span>
          <Badge className={`${s.cls} border-0`}>{s.label}</Badge>
        </div>
      </button>
      {open && (
        <div className="border-t border-border p-4 grid md:grid-cols-2 gap-6 text-sm">
          <div>
            <p className="font-semibold text-ink mb-2">Artikel ({order.bottles} Flaschen)</p>
            <ul className="space-y-1 text-ink/70">
              {(order.items || []).map((it, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span>{it.qty}× {it.name}{it.bottles ? ` (${it.bottles} Fl.)` : ""}</span>
                  <span>CHF {(it.price * it.qty).toFixed(2)}</span>
                </li>
              ))}
              {order.discount && <li className="flex justify-between text-green-700"><span>Gutschein {order.discount.code}</span><span>−CHF {order.discount.amount?.toFixed(2)}</span></li>}
            </ul>
          </div>
          <div>
            <p className="font-semibold text-ink mb-2">Lieferadresse</p>
            <p className="text-ink/70">{addr.firstName} {addr.lastName}<br />{addr.street}<br />{addr.zip} {addr.city}, {addr.country}<br />{addr.email}{addr.phone ? ` · ${addr.phone}` : ""}</p>
            {addr.note && <p className="mt-2 text-ink/60 italic">„{addr.note}"</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              {(NEXT_ACTIONS[order.status] || []).map((next) => (
                <Button key={next} data-testid={`order-action-${next}`} size="sm" variant={next === "cancelled" ? "outline" : "default"}
                  className={next === "cancelled" ? "border-red-200 text-red-600 hover:bg-red-50" : "bg-wine hover:bg-wine-dark text-white"}
                  onClick={() => onStatus(order.id, next)}>
                  → {STATUS[next].label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");

  const load = (f = filter) => adminOrders(f || undefined).then(setOrders).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await adminUpdateOrder(id, status);
      toast.success(`Status geändert: ${STATUS[status].label}`);
      load();
    } catch { toast.error("Status konnte nicht geändert werden"); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap gap-2 mb-4">
        {[["", "Alle"], ["paid", "Bezahlt"], ["pending_payment", "Ausstehend"], ["shipped", "Versendet"], ["completed", "Abgeschlossen"]].map(([v, l]) => (
          <button key={v} data-testid={`filter-${v || "all"}`} onClick={() => setFilter(v)}
            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${filter === v ? "bg-wine text-white" : "bg-white border border-border text-ink/60 hover:border-wine/40"}`}>
            {l}
          </button>
        ))}
      </div>
      {orders.length === 0 ? (
        <p className="text-ink/50 text-sm bg-white rounded-2xl border border-border p-8 text-center" data-testid="admin-orders-empty">Keine Bestellungen gefunden.</p>
      ) : (
        <div className="space-y-3">{orders.map((o) => <OrderRow key={o.id} order={o} onStatus={setStatus} />)}</div>
      )}
    </div>
  );
};
